import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { useAuth } from "../../contexts/AuthContext";
import { Task, TaskAttachment, SubtaskAttachment } from "../../types";

const ATTACHMENTS_BUCKET = "task-attachments";

export function useDeleteTaskAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (attachmentId: string): Promise<boolean> => {
    if (!user) return false;

    const tasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
    let attachment: TaskAttachment | undefined;
    for (const t of tasks) {
      attachment = t.attachments?.find((a) => a.id === attachmentId);
      if (attachment) break;
    }
    if (!attachment) return false;

    // Delete the DB row first — the DB is the source of truth.
    // If this fails, the storage object remains intact (no data loss).
    const { error } = await supabase
      .from("task_attachments")
      .delete()
      .eq("id", attachmentId);

    if (error) {
      Sentry.captureException(error, {
        extra: { mutation: "useDeleteTaskAttachment", attachmentId },
      });
      toast.error("Failed to delete attachment. Please try again.");
      return false;
    }

    // Delete from storage only after the DB row is confirmed gone.
    // If storage removal fails, the orphan is cleaned up by a periodic cron.
    if (attachment.file_path.startsWith(user.id)) {
      const { error: storageError } = await supabase.storage
        .from(ATTACHMENTS_BUCKET)
        .remove([attachment.file_path]);
      if (storageError) {
        Sentry.captureException(storageError, {
          extra: { mutation: "useDeleteTaskAttachment/storage", attachmentId },
        });
      }
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}

export function useDeleteSubtaskAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (attachmentId: string): Promise<boolean> => {
    if (!user) return false;

    const tasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
    let attachment: SubtaskAttachment | undefined;
    outer: for (const t of tasks) {
      for (const st of t.subtasks ?? []) {
        attachment = st.attachments?.find((a) => a.id === attachmentId);
        if (attachment) break outer;
      }
    }
    if (!attachment) return false;

    const { error } = await supabase
      .from("subtask_attachments")
      .delete()
      .eq("id", attachmentId);

    if (error) {
      Sentry.captureException(error, {
        extra: { mutation: "useDeleteSubtaskAttachment", attachmentId },
      });
      toast.error("Failed to delete attachment. Please try again.");
      return false;
    }

    if (attachment.file_path.startsWith(user.id)) {
      const { error: storageError } = await supabase.storage
        .from(ATTACHMENTS_BUCKET)
        .remove([attachment.file_path]);
      if (storageError) {
        Sentry.captureException(storageError, {
          extra: { mutation: "useDeleteSubtaskAttachment/storage", attachmentId },
        });
      }
    }

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}
